/**
 * The soundtrack in play (spec 07 §2): which tune plays when, following the C64's song table.
 * The AudioContext can only start after the player has touched a key, so the director is created
 * on the first key press and asks for nothing until then.
 */

import { type MusicCue, MusicPlayer } from '../../audio/music/musicPlayer';
import { type EffectContext, effectsFor } from '../../audio/sfx/triggers';
import type { SimEvent } from '../../sim/types';
import type { AudioSettings } from '../../settings';

/** The power heart (POWERUPS 24): the invincible tune for the rest of the round. */
const POWER_HEART = 24;

export class MusicDirector {
  private player: MusicPlayer | null = null;
  /** For checks: is the engine up, and what was last asked for. */
  get state(): { ready: boolean; cue: MusicCue | null } {
    return { ready: this.player !== null, cue: this.wanted };
  }

  private starting = false;
  /** What to go back to when a jingle ends. */
  private theme: MusicCue = 'main';
  private wanted: MusicCue | null = null;
  private audio: AudioSettings | null = null;

  /** The player's sound options (07 §4, 08 §4): which voice sings the music, and the volumes. */
  setAudio(a: AudioSettings): void {
    this.audio = a;
    const p = this.player;
    if (!p) return;
    p.setQuality(a.music === '1987' ? '1987' : 'remastered');
    // Music off keeps the tune running silently: the C64 only plays its effects while a tune plays.
    p.setVolume(a.music === 'off' ? 0 : a.musicVolume);
    p.setEffectsVolume(a.effectsVolume);
  }

  /** Call from a user gesture: starts the audio engine once. */
  wake(): void {
    if (this.player || this.starting) return;
    this.starting = true;
    const ctx = new AudioContext();
    void ctx.resume();
    MusicPlayer.create(ctx).then((p) => {
      this.player = p;
      p.setVolume(0.7);
      if (this.audio) this.setAudio(this.audio);
      p.onEnded = (cue) => this.ended(cue);
      if (this.wanted) p.play(this.wanted);
    }).catch((e: unknown) => console.warn('music unavailable', e));
  }

  private play(cue: MusicCue): void {
    this.wanted = cue;
    this.player?.play(cue);
  }

  private ended(cue: MusicCue): void {
    // After a jingle, back to the theme; the title tune simply ends, as on the C64.
    if (cue === 'hurryUp' || cue === 'skelMonsta' || cue === 'extend') this.play(this.theme);
  }

  title(): void {
    this.play('title');
  }

  gameStart(): void {
    this.theme = 'main';
    this.play('introAndMain');
  }

  /**
   * A new level: the theme carries on, unless the last level had hurried it (or the heart, the bonus
   * stage, the secret room had taken over). Level 100 has the boss's own tune (R§9: subtune 3).
   */
  levelStart(levelNumber: number): void {
    if (levelNumber === 100) {
      this.theme = 'bumblebee';
      this.play('bumblebee');
    } else if (levelNumber === 0) {
      this.theme = 'secretRoom';
      this.play('secretRoom');
    } else if (this.theme !== 'main') {
      this.theme = 'main';
      this.play('main');
    }
  }

  /** The ending (R§9: subtune 12, the one ending the C64 has). */
  ending(): void {
    this.theme = 'badEnding';
    this.play('badEnding');
  }

  gameOver(): void {
    this.play('gameOver');
  }

  /**
   * One sim frame's events: the sound effects they start (the C64's own, spec 07 §4; the extras if
   * chosen), and the tunes they change.
   */
  onEvents(events: readonly SimEvent[], context: EffectContext = {}): void {
    const p = this.player;
    if (p) for (const name of effectsFor(events, this.audio?.extraEffects ?? false, context)) p.effect(name);
    for (const e of events) {
      // The power heart has the invincible tune (R§9: subtune 4). The potions' bonus stage keeps the
      // tune that is playing: nothing on its path in the C64 code starts a song (docs/research/sfx.md).
      if (e.type === 'powerup' && e.item === POWER_HEART) {
        this.theme = 'bonus';
        this.play('bonus');
      }
      if (e.type === 'respawn' && this.theme !== 'main') {
        // After a death the main theme starts again at its own pace (the C64's song table).
        this.theme = 'main';
        this.play('main');
      } else if (e.type === 'hurryUp') {
        this.theme = 'mainHurry';
        this.play('hurryUp');
      } else if (e.type === 'baron') {
        this.play('skelMonsta');
      } else if (e.type === 'extend') {
        this.play('extend');
      }
    }
  }
}
